import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { OptionContractInterface } from '@modules/options-module/general/interfaces/options-contract.interface'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TuiDropdown, TuiDropdownDirective, TuiDropdownHover, TuiIcon } from '@taiga-ui/core'
import { OrderTypeSelectComponent } from '@shared/components/trading/orders/common/order-type-select/order-type-select.component'
import { OrderTermSelectComponent } from '@shared/components/trading/orders/common/order-term-select/order-term-select.component'
import { OrderQtyInputComponent } from '@shared/components/trading/orders/common/order-qty-input/order-qty-input.component'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { OrderTermEnum } from '@modules/orders-module/enums/order-term.enum'
import { TuiTextfieldControllerModule } from '@taiga-ui/legacy'
import { OrderSideEnum } from '@modules/orders-module/enums/order-side.enum'
import { Subject, takeUntil } from 'rxjs'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { OptionsAccountLevelEnum } from '@modules/user-module/enums/options-account-level.enum'
import { KycAccountStatusEnum } from '@modules/user-module/enums/kyc-account-status.enum'
import { QualificationAccountAnketaEnum } from '@modules/user-module/enums/qualification-account-anketa.enum'
import { QualificationStatusEnum } from '@modules/user-module/enums/qualification-status.enum'
import { CreateOrderInterface } from '@modules/orders-module/interfaces/create-order.interface'
import { AssetClassEnum } from '@modules/positions-module/enums/asset-class.enum'
import { HttpErrorResponse } from '@angular/common/http'
import { OptionsTradingLevelEnum } from '@modules/user-module/enums/options-trading-level.enum'
import { OptionsOrdersHelperService } from '@modules/options-module/orders/services/options-orders-helper.service'
import { OptionOrderStateEnum } from '@modules/options-module/orders/enums/option-order-state.enum'
import { OptionOrderInterface } from '@modules/options-module/orders/interfaces/option-order.interface'
import { OptionsOrdersService } from '@modules/options-module/orders/services/options-orders.service'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import { TradingLauncherService } from '@shared/services/trading-launcher.service'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { EnvironmentsConfigsLinksSupportInterface } from '@environments/core/interfaces/environments-configs-links-support.interface'
import { AlertService } from '@shared/services/alert.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'
import { PricePipe } from '@shared/pipes/price.pipe'
import { OrderPriceInputComponent } from '@shared/components/trading/orders/common/order-price-input/order-price-input.component'
import { TradeSideToggleComponent } from '@shared/components/trading/common/trade-side-toggle/trade-side-toggle.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-create-order-option',
	templateUrl: './create-order-option.component.html',
	styleUrl: './create-order-option.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		TranslatePipe,
		TuiIcon,
		OrderTypeSelectComponent,
		OrderTermSelectComponent,
		OrderQtyInputComponent,
		TuiTextfieldControllerModule,
		TuiDropdown,
		TuiDropdownDirective,
		TuiDropdownHover,
		ClickOutsideDirective,
		ButtonComponent,
		PricePipe,
		OrderPriceInputComponent,
		TradeSideToggleComponent,
	],
})
export class CreateOrderOptionsComponent implements OnInit, OnDestroy {
	protected readonly orderSideEnum: typeof OrderSideEnum = OrderSideEnum
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderTermEnum: typeof OrderTermEnum = OrderTermEnum
	protected readonly optionOrderStateEnum: typeof OptionOrderStateEnum = OptionOrderStateEnum
	protected readonly kycAccountStatusEnum: typeof KycAccountStatusEnum = KycAccountStatusEnum
	protected readonly optionsAccountLevelEnum: typeof OptionsAccountLevelEnum = OptionsAccountLevelEnum
	protected readonly qualificationAccountAnketaEnum: typeof QualificationAccountAnketaEnum =
		QualificationAccountAnketaEnum
	protected readonly qualificationStatusEnum: typeof QualificationStatusEnum = QualificationStatusEnum
	protected readonly assetClassEnum: typeof AssetClassEnum = AssetClassEnum
	protected readonly optionsTradingLevelEnum: typeof OptionsTradingLevelEnum = OptionsTradingLevelEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly orderTerms: OrderTermEnum[] = [this.orderTermEnum.DAY, this.orderTermEnum.GTC]
	protected readonly orderTypes: OrderTypeEnum[] = [this.orderTypeEnum.MARKET, this.orderTypeEnum.LIMIT]
	protected readonly reasonsTradingOption: string[] = [
		'noBrokerAccount',
		'kycIncomplete',
		'documentsNotSigned',
		'moderationPending',
		'optionsNotEnabled',
		'personalDataUpdating',
	]

	public optionContract: InputSignal<OptionContractInterface | null> = input<OptionContractInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _optionsOrderService: OptionsOrdersService = inject<OptionsOrdersService>(OptionsOrdersService)
	private readonly _optionsOrderHelperService: OptionsOrdersHelperService =
		inject<OptionsOrdersHelperService>(OptionsOrdersHelperService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _tradingLauncherService: TradingLauncherService =
		inject<TradingLauncherService>(TradingLauncherService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)

	private _userDetail: UserDetailInterface | null = null

	protected form: FormGroup | null = null

	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal<AlpacaAccountInterface | null>(null)
	protected isLoadingCreate: WritableSignal<boolean> = signal<boolean>(false)

	protected canBuy: WritableSignal<boolean> = signal<boolean>(false)
	protected canSell: WritableSignal<boolean> = signal<boolean>(false)

	protected approximateSum: WritableSignal<number> = signal<number>(0)

	protected formattedOptionContractTicker: Signal<string | null> = computed<string | null>((): string | null => {
		const optionContract: OptionContractInterface | null = this.optionContract()
		if (!optionContract) return null

		const ticker: string = optionContract.details.ticker
		return ticker.startsWith('O:') ? ticker.slice(2) : ticker
	})

	get isInvalidOrder(): boolean {
		const order: CreateOrderInterface | null = this._getOrderForCreate()

		return (
			(order?.side === this.orderSideEnum.BUY && !this.canBuy()) ||
			(order?.side === this.orderSideEnum.SELL && !this.canSell()) ||
			this.form?.invalid ||
			this.isLoadingCreate() ||
			(order?.type === this.orderTypeEnum.MARKET && this.marketStatus !== this.marketStatusEnum.marketOpen)
		)
	}

	get supportByLang(): EnvironmentsConfigsLinksSupportInterface | undefined {
		return this._environmentsHelperService.linksByLang?.support
	}

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}
	private _calcApproximateSum(): void {
		const optionContract: OptionContractInterface | null = this.optionContract()
		if (!optionContract) return

		const price: number = this.form?.get('limit_price')?.value ?? 0
		const qty: number = this.form?.get('qty')?.value ?? 0
		const sharePerContract: number = optionContract.details.shares_per_contract

		this.approximateSum.set(price * qty * sharePerContract)
	}

	private _listenFormForApproximateSum(): void {
		this._calcApproximateSum()
		this.form?.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((): void => {
			this._calcApproximateSum()
		})
	}

	private _launchUserDetail(): void {
		this._tradingLauncherService
			.launchUserDetail$()
			.pipe(takeUntil(this._destroy$))
			.subscribe(([userDetail]: [UserDetailInterface | null, boolean]): void => {
				this._userDetail = userDetail
				this._checkCanBuySell()
			})
	}

	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	private _initForm(): void {
		const optionContract: OptionContractInterface | null = this.optionContract()
		if (!optionContract) return

		this.form = this._formBuilder.group({
			side: [this.orderSideEnum.BUY],
			type: [this.orderTypeEnum.LIMIT],
			limit_price: [optionContract.last_quote.ask],
			qty: [1],
			time_in_force: [this.orderTermEnum.GTC],
		})
	}

	private _resetForm(): void {
		const optionContract: OptionContractInterface | null = this.optionContract()
		if (!optionContract) return

		this.form?.patchValue({
			side: this.orderSideEnum.BUY,
			type: this.orderTypeEnum.LIMIT,
			limit_price: [optionContract.last_quote.ask],
			qty: 1,
			time_in_force: this.orderTermEnum.GTC,
		})
	}

	private _updatePrice(): void {
		const optionContract: OptionContractInterface | null = this.optionContract()
		if (!optionContract) return

		const orderSide: OrderSideEnum | null = this.form?.get('side')?.value
		if (!orderSide) return

		const price: number =
			orderSide === this.orderSideEnum.BUY ? optionContract.last_quote.ask : optionContract.last_quote.bid

		this.form?.get('limit_price')?.patchValue(price)
	}

	private _checkInvalidQualifications(): boolean {
		const userDetail: UserDetailInterface | null = this._userDetail
		if (!userDetail) return true

		return (
			(userDetail.qualifications === this.qualificationAccountAnketaEnum.retailInvestor &&
				userDetail.qualification_status === this.qualificationStatusEnum.confirmed) ||
			(userDetail.qualifications === this.qualificationAccountAnketaEnum.professional &&
				userDetail.qualification_status === this.qualificationStatusEnum.pending)
		)
	}

	private _getOrderForCreate(): CreateOrderInterface | null {
		const formattedOptionContractTicker: string | null = this.formattedOptionContractTicker()
		const formRawValue: CreateOrderInterface | undefined = this.form?.getRawValue()
		if (!formRawValue || !formattedOptionContractTicker) return null

		const { limit_price, ...formValue } = formRawValue

		return {
			...formValue,
			symbol: formattedOptionContractTicker,
			asset_class: this.assetClassEnum.US_OPTION,
			...(formValue.type === this.orderTypeEnum.LIMIT && { limit_price }),
		}
	}

	private _createOrder(): void {
		const orderForCreate: CreateOrderInterface | null = this._getOrderForCreate()
		if (!orderForCreate) return

		this.isLoadingCreate.set(true)
		this._optionsOrderService
			.createOptionOrder(orderForCreate)
			.pipe(takeUntil(this._destroy$))
			.subscribe(
				(response: OptionOrderInterface): void => {
					this._optionsOrderHelperService.moveOptionOrder(response, this.optionOrderStateEnum.OPEN)
					this._alertService.success(
						this._translateService.instant('optionsCreateOrder.accepted', {
							symbol: this.formattedOptionContractTicker(),
						}),
					)
					this._resetForm()
					this.isLoadingCreate.set(false)
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error?.error?.error?.message ||
							this._translateService.instant('options.errors.failCreateOrder'),
					)
					this.isLoadingCreate.set(false)
				},
			)
	}

	private _checkCanBuySell(): void {
		const userDetail: UserDetailInterface | null = this._userDetail
		if (!userDetail) return

		const isActionRequired: boolean = userDetail.kyc_account_status === this.kycAccountStatusEnum.ACTION_REQUIRED
		if (isActionRequired) return

		const OAL: OptionsAccountLevelEnum | null = userDetail.options_approved_level
		const OTL: OptionsTradingLevelEnum | null = userDetail.options_trading_level

		if (OAL === null && OTL === null) {
			this.canBuy.set(false)
			this.canSell.set(false)
		}

		if (OAL === this.optionsAccountLevelEnum.SECOND && OTL === null) {
			this.canBuy.set(true)
			this.canSell.set(true)
		}

		if (OAL === this.optionsAccountLevelEnum.FIRST && OTL === null) {
			this.canBuy.set(false)
			this.canSell.set(true)
		}

		if (OAL === this.optionsAccountLevelEnum.SECOND && OTL === this.optionsTradingLevelEnum.SECOND) {
			this.canBuy.set(true)
			this.canSell.set(true)
		}

		if (OAL === this.optionsAccountLevelEnum.SECOND && OTL === this.optionsTradingLevelEnum.FIRST) {
			this.canBuy.set(false)
			this.canSell.set(true)
		}

		if (OAL === this.optionsAccountLevelEnum.SECOND && OTL === this.optionsTradingLevelEnum.ZERO) {
			this.canBuy.set(false)
			this.canSell.set(false)
		}

		if (OAL === this.optionsAccountLevelEnum.FIRST && OTL === this.optionsTradingLevelEnum.FIRST) {
			this.canBuy.set(false)
			this.canSell.set(true)
		}

		if (OAL === this.optionsAccountLevelEnum.FIRST && OTL === this.optionsTradingLevelEnum.ZERO) {
			this.canBuy.set(false)
			this.canSell.set(false)
		}
	}

	private _listenTypeControl(): void {
		this.form
			?.get('type')
			?.valueChanges.pipe(takeUntil(this._destroy$))
			.subscribe((type: OrderTypeEnum): void => {
				if (type === this.orderTypeEnum.MARKET && this.marketStatus !== this.marketStatusEnum.marketOpen) {
					this._alertService.warning(
						this._translateService.instant('optionsCreateOrder.warnings.closeNotAvailable'),
					)
				}
			})
	}

	protected toggleOrderSide(orderSide: OrderSideEnum): void {
		this.form?.get('side')?.patchValue(orderSide)
		this._updatePrice()
	}

	protected onSubmit(): void {
		if (this._checkInvalidQualifications()) {
			this._alertService.error(this._translateService.instant('optionsCreateOrder.errors.notAllowed'))
			return
		}

		this._createOrder()
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._launchUserDetail()
		this._listenAlpacaAccount()
		this._initForm()
		this._listenTypeControl()
		this._listenFormForApproximateSum()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
